/** @format */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import nigeriaData from "../../data/nigeriaLocations.json";

/**
 * LocationPicker - Cascading Country > State > City picker for Nigeria addresses
 *
 * @param {Object} props
 * @param {string} props.country - Selected country (defaults to "Nigeria")
 * @param {string} props.state - Selected state
 * @param {string} props.city - Selected city
 * @param {function} props.onCountryChange - Callback when country changes
 * @param {function} props.onStateChange - Callback when state changes
 * @param {function} props.onCityChange - Callback when city changes
 * @param {Object} props.errors - Error messages { country, state, city }
 * @param {string} props.countryLabel - Label for country field
 * @param {string} props.stateLabel - Label for state field
 * @param {string} props.cityLabel - Label for city field
 * @param {boolean} props.showCountry - Whether to show country field
 */
const LocationPicker = ({ country = "Nigeria", state = "", city = "", onCountryChange, onStateChange, onCityChange, errors = {}, countryLabel = "Country", stateLabel = "State", cityLabel = "City", showCountry = true }) => {
	const [stateSearch, setStateSearch] = useState("");
	const [citySearch, setCitySearch] = useState("");
	const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
	const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

	// Get list of states
	const allStates = useMemo(() => nigeriaData.states.map((s) => s.name).sort(), []);

	// Filter states by search
	const filteredStates = useMemo(() => {
		if (!stateSearch) return allStates;
		return allStates.filter((s) => s.toLowerCase().includes(stateSearch.toLowerCase()));
	}, [allStates, stateSearch]);

	// Get list of cities for selected state
	const allCities = useMemo(() => {
		if (!state) return [];
		const stateData = nigeriaData.states.find((s) => s.name === state);
		return stateData ? stateData.cities.sort() : [];
	}, [state]);

	// Filter cities by search
	const filteredCities = useMemo(() => {
		if (!citySearch) return allCities;
		return allCities.filter((c) => c.toLowerCase().includes(citySearch.toLowerCase()));
	}, [allCities, citySearch]);

	// Handle state selection
	const handleStateSelect = useCallback(
		(selectedState) => {
			onStateChange(selectedState);
			onCityChange(""); // Reset city when state changes
			setStateSearch("");
			setStateDropdownOpen(false);
		},
		[onStateChange, onCityChange],
	);

	// Handle city selection
	const handleCitySelect = useCallback(
		(selectedCity) => {
			onCityChange(selectedCity);
			setCitySearch("");
			setCityDropdownOpen(false);
		},
		[onCityChange],
	);

	// Close dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (!e.target.closest(".location-picker-dropdown")) {
				setStateDropdownOpen(false);
				setCityDropdownOpen(false);
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	return (
		<>
			{/* Country - Fixed to Nigeria */}
			{showCountry && (
				<div className='col-md-6 col-12'>
					<div className='prime-input'>
						<label>{countryLabel}</label>
						<input type='text' className='form-input' value={country} disabled readOnly style={{ backgroundColor: "#2a2a2a", cursor: "not-allowed" }} />
						{errors.country && <p className='errorMessage'>{errors.country}</p>}
					</div>
				</div>
			)}

			{/* State - Searchable Dropdown */}
			<div className='col-md-6 col-12'>
				<div className='prime-input location-picker-dropdown'>
					<label>{stateLabel}</label>
					<div style={{ position: "relative" }}>
						<input
							type='text'
							className='form-input'
							placeholder='Search & Select State'
							value={stateDropdownOpen ? stateSearch : state}
							onChange={(e) => {
								setStateSearch(e.target.value);
								if (!stateDropdownOpen) setStateDropdownOpen(true);
							}}
							onFocus={() => setStateDropdownOpen(true)}
							onClick={(e) => e.stopPropagation()}
						/>
						<i className='fa-solid fa-chevron-down' style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#999", pointerEvents: "none" }} />
						{stateDropdownOpen && (
							<div
								style={{
									position: "absolute",
									top: "100%",
									left: 0,
									right: 0,
									maxHeight: "200px",
									overflowY: "auto",
									backgroundColor: "#1a1a1a",
									border: "1px solid #333",
									borderRadius: "8px",
									zIndex: 1000,
									marginTop: "4px",
								}}>
								{filteredStates.length > 0 ? (
									filteredStates.map((s) => (
										<div
											key={s}
											onClick={(e) => {
												e.stopPropagation();
												handleStateSelect(s);
											}}
											style={{
												padding: "10px 14px",
												cursor: "pointer",
												backgroundColor: state === s ? "rgba(229, 74, 19, 0.2)" : "transparent",
												color: state === s ? "#E54A13" : "#fff",
											}}
											onMouseEnter={(e) => (e.target.style.backgroundColor = state === s ? "rgba(229, 74, 19, 0.3)" : "#2a2a2a")}
											onMouseLeave={(e) => (e.target.style.backgroundColor = state === s ? "rgba(229, 74, 19, 0.2)" : "transparent")}>
											{s}
										</div>
									))
								) : (
									<div style={{ padding: "10px 14px", color: "#666", textAlign: "center" }}>No states found</div>
								)}
							</div>
						)}
					</div>
					{errors.state && <p className='errorMessage'>{errors.state}</p>}
				</div>
			</div>

			{/* City - Searchable Dropdown (enabled only when state is selected) */}
			<div className='col-md-6 col-12'>
				<div className='prime-input location-picker-dropdown'>
					<label>{cityLabel}</label>
					<div style={{ position: "relative" }}>
						<input
							type='text'
							className='form-input'
							placeholder={state ? "Search & Select City" : "Select State first"}
							value={cityDropdownOpen ? citySearch : city}
							onChange={(e) => {
								setCitySearch(e.target.value);
								if (!cityDropdownOpen) setCityDropdownOpen(true);
							}}
							onFocus={() => state && setCityDropdownOpen(true)}
							onClick={(e) => e.stopPropagation()}
							disabled={!state}
							style={!state ? { backgroundColor: "#2a2a2a", cursor: "not-allowed" } : {}}
						/>
						<i className='fa-solid fa-chevron-down' style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#999", pointerEvents: "none" }} />
						{cityDropdownOpen && state && (
							<div
								style={{
									position: "absolute",
									top: "100%",
									left: 0,
									right: 0,
									maxHeight: "200px",
									overflowY: "auto",
									backgroundColor: "#1a1a1a",
									border: "1px solid #333",
									borderRadius: "8px",
									zIndex: 1000,
									marginTop: "4px",
								}}>
								{filteredCities.length > 0 ? (
									filteredCities.map((c) => (
										<div
											key={c}
											onClick={(e) => {
												e.stopPropagation();
												handleCitySelect(c);
											}}
											style={{
												padding: "10px 14px",
												cursor: "pointer",
												backgroundColor: city === c ? "rgba(229, 74, 19, 0.2)" : "transparent",
												color: city === c ? "#E54A13" : "#fff",
											}}
											onMouseEnter={(e) => (e.target.style.backgroundColor = city === c ? "rgba(229, 74, 19, 0.3)" : "#2a2a2a")}
											onMouseLeave={(e) => (e.target.style.backgroundColor = city === c ? "rgba(229, 74, 19, 0.2)" : "transparent")}>
											{c}
										</div>
									))
								) : (
									<div style={{ padding: "10px 14px", color: "#666", textAlign: "center" }}>No cities found</div>
								)}
							</div>
						)}
					</div>
					{errors.city && <p className='errorMessage'>{errors.city}</p>}
				</div>
			</div>
		</>
	);
};

export default LocationPicker;
